import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, ExternalLink, Eye, Code, Settings, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Posit } from "@/types/posit";
import { Json } from "@/integrations/supabase/types";

interface EmbedConfig {
  theme: 'light' | 'dark' | 'auto';
  showBranding: boolean;
  showExpandButton: boolean;
  maxHeight: string;
  maxWidth: string;
  borderRadius: string;
  primaryColor: string;
  allowedDomains: string[];
}

interface EmbedConfigDialogProps {
  posit: Posit;
  open: boolean;
  onClose: () => void;
}

const EmbedConfigDialog = ({ posit, open, onClose }: EmbedConfigDialogProps) => {
  const { toast } = useToast();
  const [isEnabled, setIsEnabled] = useState(false);
  const [config, setConfig] = useState<EmbedConfig>({
    theme: 'auto',
    showBranding: true,
    showExpandButton: true,
    maxHeight: '400px',
    maxWidth: '100%',
    borderRadius: '12px',
    primaryColor: '#000000',
    allowedDomains: ['*']
  });
  const [newDomain, setNewDomain] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load existing embed configuration
  useEffect(() => {
    const loadEmbedConfig = async () => {
      try {
        const { data, error } = await supabase
          .from("posits")
          .select("embed_enabled, embed_config, embed_domains")
          .eq("id", posit.id)
          .single();

        if (error) throw error;

        if (data) {
          setIsEnabled(data.embed_enabled || false);
          
          if (data.embed_config && typeof data.embed_config === 'object') {
            const savedConfig = data.embed_config as unknown as EmbedConfig;
            setConfig(savedConfig);
          }

          if (data.embed_domains) {
            setConfig(prev => ({
              ...prev,
              allowedDomains: data.embed_domains || []
            }));
          }
        }
      } catch (error) {
      }
    };

    if (open) {
      loadEmbedConfig();
    }
  }, [open, posit.id]);

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from("posits")
        .update({
          embed_enabled: isEnabled,
          embed_config: config as unknown as Json,
          embed_domains: config.allowedDomains,
          embed_code: generateEmbedCode()
        })
        .eq("id", posit.id);

      if (error) throw error;

      toast({
        title: "Embed settings saved",
        description: "Your posit embed configuration has been updated.",
      });
      
      onClose();
    } catch (error) {
      toast({
        title: "Error saving settings",
        description: "Failed to save embed settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const generateEmbedCode = () => {
    const params = new URLSearchParams();
    if (config.theme !== 'auto') params.set('theme', config.theme);
    if (!config.showBranding) params.set('branding', 'false');
    if (!config.showExpandButton) params.set('expand', 'false');
    if (config.maxHeight !== '400px') params.set('height', config.maxHeight);
    if (config.maxWidth !== '100%') params.set('width', config.maxWidth);
    if (config.borderRadius !== '12px') params.set('radius', config.borderRadius);
    if (config.primaryColor !== '#000000') params.set('color', config.primaryColor);
    
    const embedUrl = `${window.location.origin}/embed/posit/${posit.id}${params.toString() ? '?' + params.toString() : ''}`;
    
    return `<iframe 
  src="${embedUrl}" 
  width="${config.maxWidth}" 
  height="${config.maxHeight}" 
  frameborder="0" 
  style="border-radius: ${config.borderRadius}; max-width: 100%;"
  title="${posit.title} - Posit">
</iframe>`;
  };

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      toast({
        title: "Copied!",
        description: "Embed code copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy embed code.",
        variant: "destructive",
      });
    }
  };

  const copyEmbedUrl = async () => {
    const params = new URLSearchParams();
    if (config.theme !== 'auto') params.set('theme', config.theme);
    if (!config.showBranding) params.set('branding', 'false');
    if (!config.showExpandButton) params.set('expand', 'false');
    if (config.maxHeight !== '400px') params.set('height', config.maxHeight);
    if (config.maxWidth !== '100%') params.set('width', config.maxWidth);
    if (config.borderRadius !== '12px') params.set('radius', config.borderRadius);
    if (config.primaryColor !== '#000000') params.set('color', config.primaryColor);
    
    const embedUrl = `${window.location.origin}/embed/posit/${posit.id}${params.toString() ? '?' + params.toString() : ''}`;
    
    try {
      await navigator.clipboard.writeText(embedUrl);
      toast({
        title: "Copied!",
        description: "Embed URL copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy embed URL.",
        variant: "destructive",
      });
    }
  };

  const previewEmbedUrl = () => {
    const params = new URLSearchParams();
    if (config.theme !== 'auto') params.set('theme', config.theme);
    if (!config.showBranding) params.set('branding', 'false');
    if (!config.showExpandButton) params.set('expand', 'false');
    if (config.maxHeight !== '400px') params.set('height', config.maxHeight);
    if (config.maxWidth !== '100%') params.set('width', config.maxWidth);
    if (config.borderRadius !== '12px') params.set('radius', config.borderRadius);
    if (config.primaryColor !== '#000000') params.set('color', config.primaryColor);
    
    const embedUrl = `${window.location.origin}/embed/posit/${posit.id}${params.toString() ? '?' + params.toString() : ''}`;
    window.open(embedUrl, '_blank');
  };

  const addDomain = () => {
    if (newDomain.trim() && !config.allowedDomains.includes(newDomain.trim())) {
      setConfig(prev => ({
        ...prev,
        allowedDomains: [...prev.allowedDomains.filter(d => d !== '*'), newDomain.trim()]
      }));
      setNewDomain('');
    }
  };

  const removeDomain = (domain: string) => {
    setConfig(prev => ({
      ...prev,
      allowedDomains: prev.allowedDomains.filter(d => d !== domain)
    }));
  };

  const allowAllDomains = () => {
    setConfig(prev => ({
      ...prev,
      allowedDomains: ['*']
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Embed Configuration</DialogTitle>
          <DialogDescription>
            Configure how your Posit appears when embedded on other websites.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="code">
              <Code className="w-4 h-4 mr-2" />
              Embed Code
            </TabsTrigger>
            <TabsTrigger value="preview">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enable-embed">Enable Embedding</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow this Posit to be embedded on other websites
                  </p>
                </div>
                <Switch
                  id="enable-embed"
                  checked={isEnabled}
                  onCheckedChange={setIsEnabled}
                />
              </div>

              {isEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="theme">Theme</Label>
                      <Select value="dark" disabled>
                        <SelectTrigger>
                          <SelectValue placeholder="Dark Mode (Only)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dark">Dark</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="primary-color">Primary Color</Label>
                      <Input
                        id="primary-color"
                        type="color"
                        value={config.primaryColor}
                        onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="max-height">Max Height</Label>
                      <Input
                        id="max-height"
                        value={config.maxHeight}
                        onChange={(e) => setConfig(prev => ({ ...prev, maxHeight: e.target.value }))}
                        placeholder="400px"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="max-width">Max Width</Label>
                      <Input
                        id="max-width"
                        value={config.maxWidth}
                        onChange={(e) => setConfig(prev => ({ ...prev, maxWidth: e.target.value }))}
                        placeholder="100%"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="border-radius">Border Radius</Label>
                    <Input
                      id="border-radius"
                      value={config.borderRadius}
                      onChange={(e) => setConfig(prev => ({ ...prev, borderRadius: e.target.value }))}
                      placeholder="12px"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Branding</Label>
                        <p className="text-sm text-muted-foreground">
                          Display "Powered by Posit" in the embed
                        </p>
                      </div>
                      <Switch
                        checked={config.showBranding}
                        onCheckedChange={(checked) => setConfig(prev => ({ ...prev, showBranding: checked }))}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Show Expand Button</Label>
                        <p className="text-sm text-muted-foreground">
                          Allow users to expand the embed
                        </p>
                      </div>
                      <Switch
                        checked={config.showExpandButton}
                        onCheckedChange={(checked) => setConfig(prev => ({ ...prev, showExpandButton: checked }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label>Allowed Domains</Label>
                      <p className="text-sm text-muted-foreground">
                        Restrict embedding to specific domains. Leave empty or use * to allow all domains.
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="example.com"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addDomain();
                          }
                        }}
                      />
                      <Button onClick={addDomain}>Add</Button>
                      <Button variant="outline" onClick={allowAllDomains}>
                        Allow All
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {config.allowedDomains.map((domain) => (
                        <Badge key={domain} variant="secondary" className="flex items-center gap-1">
                          {domain}
                          {domain !== '*' && (
                            <button
                              onClick={() => removeDomain(domain)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="code" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>HTML Embed Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Textarea
                    value={generateEmbedCode()}
                    readOnly
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <div className="flex gap-2">
                    <Button onClick={copyEmbedCode}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Embed Code
                    </Button>
                    <Button variant="outline" onClick={copyEmbedUrl}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy URL
                    </Button>
                    <Button variant="outline" onClick={previewEmbedUrl}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Embed Preview</CardTitle>
                <DialogDescription>
                  This is how your Posit will appear when embedded
                </DialogDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div 
                    className="mx-auto"
                    style={{ 
                      maxWidth: config.maxWidth, 
                      height: config.maxHeight,
                      borderRadius: config.borderRadius 
                    }}
                  >
                    <iframe
                      src={`${window.location.origin}/embed/posit/${posit.id}?theme=${config.theme}&branding=${config.showBranding}&expand=${config.showExpandButton}&height=${config.maxHeight}&width=${config.maxWidth}&radius=${config.borderRadius}&color=${encodeURIComponent(config.primaryColor)}`}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title={`${posit.title} - Posit Preview`}
                      style={{ borderRadius: config.borderRadius }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmbedConfigDialog; 